import React from 'react';
import { motion } from 'framer-motion';
import { Check, X, Star, ArrowRight, MessageCircle, Sparkles } from 'lucide-react';
import { trackAddToCart } from '../lib/metaPixel';
interface Feature {
  text: string;
  included: boolean;
}
interface PlanProps {
  name: string;
  tagline: string;
  features: Feature[];
  ctaLabel: string;
  ctaHref: string;
  recommended?: boolean;
  delay: number;
}
function PlanCard({
  name,
  tagline,
  features,
  ctaLabel,
  ctaHref,
  recommended,
  delay
}: PlanProps) {
  return <motion.div initial={{
    opacity: 0,
    y: 30
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true,
    margin: '-50px'
  }} transition={{
    duration: 0.6,
    delay
  }} className={`relative rounded-3xl p-8 sm:p-10 flex flex-col ${recommended ? 'bg-gradient-to-b from-ed-dark to-ed-darker border-2 border-ed-green shadow-[0_0_60px_rgba(163,255,18,0.2)]' : 'bg-ed-dark border border-zinc-800'}`} data-id="element-366">
      {recommended && <div className="absolute -top-3.5 left-1/2 -translate-x-1/2" data-id="element-367">
          <div className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full bg-ed-green text-ed-black text-xs font-black uppercase tracking-widest shadow-[0_0_20px_rgba(163,255,18,0.5)]" data-id="element-368">
            <Star className="w-3.5 h-3.5" fill="currentColor" data-id="element-369" />
            Recomendado
          </div>
        </div>}

      <div className="mb-6" data-id="element-370">
        <h3 className={`text-xs font-black tracking-widest uppercase mb-3 ${recommended ? 'text-ed-green' : 'text-zinc-500'}`} data-id="element-371">
          {recommended ? 'Plan completo' : 'Plan básico'}
        </h3>
        <h4 className="text-2xl sm:text-3xl font-black text-white uppercase leading-tight mb-3" data-id="element-372">
          {name}
        </h4>
        <p className="text-zinc-400 leading-relaxed" data-id="element-373">{tagline}</p>
      </div>

      <ul className="space-y-3.5 mb-8 flex-grow" data-id="element-374">
        {features.map((f, i) => <li key={i} className="flex items-start gap-3" data-id="element-375">
            <div className={`w-5 h-5 rounded-md flex items-center justify-center shrink-0 mt-0.5 ${f.included ? 'bg-ed-green/15 border border-ed-green/40' : 'bg-zinc-900 border border-zinc-800'}`} data-id="element-376">
              {f.included ? <Check className="w-3 h-3 text-ed-green" strokeWidth={3} data-id="element-377" /> : <X className="w-3 h-3 text-zinc-600" strokeWidth={3} data-id="element-378" />}
            </div>
            <span className={`text-sm leading-snug ${f.included ? 'text-zinc-200 font-medium' : 'text-zinc-600 line-through'}`} data-id="element-379">
              {f.text}
            </span>
          </li>)}
      </ul>

      <a href={ctaHref} target="_blank" rel="noopener noreferrer" onClick={() => trackAddToCart({
      content_name: name,
      content_ids: [ctaHref.split('/cart/')[1]?.split(':')[0] || '']
    })} className={`inline-flex flex-col items-center justify-center px-6 py-3.5 rounded-xl uppercase transition-all ${recommended ? 'bg-ed-green text-ed-black hover:bg-ed-green-dark glow-green hover:scale-[1.02] active:scale-[0.98]' : 'bg-transparent border-2 border-zinc-700 text-white hover:border-ed-green hover:bg-ed-green/5'}`} data-id="element-380">
        <span className="inline-flex items-center gap-2 font-black tracking-wide text-sm" data-id="element-381">
          {ctaLabel}
          {recommended ? <ArrowRight className="w-5 h-5" data-id="element-382" /> : <MessageCircle className="w-4 h-4" data-id="element-383" />}
        </span>
        {recommended && <span className="inline-flex items-center gap-1.5 bg-ed-black text-ed-green text-[11px] font-black tracking-wide normal-case px-2.5 py-1 rounded-full mt-1.5" data-id="element-384">
            <Sparkles className="w-3 h-3" data-id="element-385" />
            12 cuotas sin interés
          </span>}
      </a>
    </motion.div>;
}
export function Plans() {
  const basicFeatures: Feature[] = [{
    text: '3 módulos completos en PDF',
    included: true
  }, {
    text: 'Bonos exclusivos incluidos',
    included: true
  }, {
    text: 'Acceso al material',
    included: true
  }, {
    text: 'Clases en vivo con docente',
    included: false
  }, {
    text: 'Acceso a clases grabadas',
    included: false
  }, {
    text: 'Comunidad de alumnos',
    included: false
  }, {
    text: 'Soporte directo del profesor',
    included: false
  }, {
    text: 'Actualizaciones y descuentos futuros',
    included: false
  }];
  const proFeatures: Feature[] = [{
    text: '3 módulos completos en PDF',
    included: true
  }, {
    text: 'Bonos exclusivos incluidos',
    included: true
  }, {
    text: 'Clases en vivo con docente',
    included: true
  }, {
    text: 'Acceso a clases grabadas sin límite',
    included: true
  }, {
    text: 'Comunidad activa de alumnos',
    included: true
  }, {
    text: 'Soporte directo del profesor',
    included: true
  }, {
    text: 'Actualizaciones de contenido incluidas',
    included: true
  }, {
    text: 'Descuentos en formaciones futuras',
    included: true
  }];
  return <section id="planes" className="py-24 bg-ed-black relative border-t border-white/5 overflow-hidden" data-id="element-386">
      <div className="absolute inset-0 bg-radial-green opacity-30 pointer-events-none" data-id="element-387"></div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-388">
        <div className="text-center mb-16 max-w-2xl mx-auto" data-id="element-389">
          <span className="text-ed-green font-bold tracking-widest uppercase text-sm mb-3 block" data-id="element-390">
            Elegí tu camino
          </span>
          <h2 className="text-4xl sm:text-5xl font-black uppercase tracking-tight text-white mb-4" data-id="element-391">
            Dos planes. <br className="sm:hidden" data-id="element-392" />
            Una <span className="text-ed-green" data-id="element-393">transformación.</span>
          </h2>
          <p className="text-zinc-400 text-lg" data-id="element-394">
            Empezá con el plan que más se adapte a vos. Siempre podés sumar más
            después.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-6 lg:gap-8 max-w-5xl mx-auto md:items-stretch pt-4" data-id="element-395">
          <PlanCard name="Para arrancar" tagline="Acceso al material esencial para empezar a aprender por tu cuenta." features={basicFeatures} ctaLabel="Acceder a Plan Profesional" ctaHref="https://academiaed.com/cart/52970052682037:1" delay={0.1} data-id="element-396" />
          <PlanCard name="Formación Profesional Completa" tagline="La experiencia integral con clases en vivo, comunidad y acompañamiento real." features={proFeatures} ctaLabel="Acceder a Plan Premium" ctaHref="https://academiaed.com/cart/52970094002485:1" recommended delay={0.2} data-id="element-397" />
        </div>
      </div>
    </section>;
}